import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { discountRefusal, DISCOUNTS_COPY as T } from "./discounts";
import type { Editor } from "./StrategyParts";

/**
 * What both Discounts panes share (C1 v1.22): the one write idiom and the pane's load states. The write reports on the pane's result
 * line (the Strategies editor's `Editor`): its sentence on success, C2's refusal in words on error (`discountRefusal`: Config's
 * `writeRefusal`, with a write's own line when the API never answered), and after either the pane's one refresh, awaited so a control
 * disabled while the write is pending cannot send twice.
 */
export function useDiscountWrite<V, A>(editor: Editor, write: (vars: V) => Promise<A>, done: (answer: A, vars: V) => string,
  refresh: (client: QueryClient) => Promise<void>, settled?: () => void) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: (answer: A, vars: V) => editor.report({ tone: "ok", text: done(answer, vars) }),
    onError: (error: unknown) => editor.report({ tone: "err", text: discountRefusal(error, editor.areaName) }),
    onSettled: async () => { settled?.(); await refresh(client); },
  });
}

/** A read that failed: v26's pane banner, the sentence and Retry (which changes nothing, so a view-only person keeps it). */
export function Failed({ sentence, onRetry }: { sentence: string; onRetry: () => void }) {
  return (
    <Alert role="alert" variant="destructive" className="flex flex-wrap items-center justify-between gap-2">
      <AlertDescription>{sentence}</AlertDescription>
      <Button size="sm" variant="outline" className="text-ks-ink" data-nonmutating onClick={onRetry}>{T.retry}</Button>
    </Alert>
  );
}

/** v26's loading state: four rows. */
export function PaneSkeleton() {
  return <Card><CardContent className="flex flex-col gap-2 py-4">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-7 w-full" />)}</CardContent></Card>;
}
